import { TbExternalLink } from 'react-icons/tb'
import { NavLink } from 'react-router-dom'

import { cva, cx, RecipeVariantProps } from 'styled-system/css'
import { Box, Grid, GridProps, HStack } from 'styled-system/jsx'
import { Heading, HeadingProps } from 'ui-kit/heading'
import { Icon } from 'ui-kit/icon'
import { Text } from 'ui-kit/text'

import classes from './DocGrid.module.css'
import { getDocRelativeLink, useArticleData } from './helpers'

const gridBlockRecipe = cva({
  base: {
    display: 'grid',
    gridTemplateColumns: 'repeat(1, minmax(0, 1fr))',
    columnGap: '5',
    rowGap: '4',
  },
  variants: {
    cols: {
      2: {
        md: {
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        },
      },
      3: {
        md: {
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        },
        lg: {
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        },
      },
    },
  },
})

function DocGridRoot({
  children,
  className,
  cols = 3,
  ...props
}: GridProps & RecipeVariantProps<typeof gridBlockRecipe>) {
  const classnames = gridBlockRecipe({ cols })
  return (
    <Grid className={cx(classnames, className)} {...props}>
      {children}
    </Grid>
  )
}

const gridItemRecipe = cva({
  base: {
    position: 'relative',
    fontWeight: 'normal',
    display: 'block',
    paddingLeft: '7',
    paddingRight: '7',
    paddingTop: '5',
    paddingBottom: '5',
    borderRadius: 'xl',
    height: 'full',
    borderWidth: '1',
    borderColor: 'border.muted',
    bgGradient: 'to-b',
    gradientFrom: 'bg.canvas',
    gradientTo: 'bg.subtle',
    _hover: {
      textDecoration: 'none',
      '&:has(a:hover)': {
        borderColor: 'border.outline',
      },
    },
  },
  variants: {
    highlight: {
      true: {
        borderColor: 'border.default',
        gradientFrom: 'bg.default',
        gradientTo: 'bg.subtle',
      },
    },
  },
})

function DocLink({
  path,
  title: customTitle,
  description: customDescription,
  highlight = false,
  target,
  openAsNewPage = false,
}: {
  path: string
  title?: string
  description?: string
  highlight?: boolean
  target?: string
  openAsNewPage?: boolean
}) {
  const classnames = gridItemRecipe({ highlight })
  const { title, description: defaultDescription } = useArticleData(path)

  const to = getDocRelativeLink(path)

  if (!title) {
    return null
  }

  const description = customDescription ?? defaultDescription

  return (
    <Box className={classnames}>
      <HStack justifyContent='space-between' alignItems='start'>
        <StyledHeading>
          <NavLink
            to={to}
            target={openAsNewPage ? '_blank' : target}
            className={classes.item_titleLink}
          >
            {customTitle || title}
          </NavLink>
        </StyledHeading>
        {openAsNewPage && (
          <Icon
            size='sm'
            color='fg.subtle'
            marginInlineEnd='-4'
            marginBlockStart='-2'
          >
            <TbExternalLink />
          </Icon>
        )}
      </HStack>
      {description && (
        <Text className={classes.item_description}>{description}</Text>
      )}
    </Box>
  )
}

function ExternalLink({
  href,
  title,
  className,
  highlight,
  description,
}: {
  href: string
  title: string
  className?: string
  description?: string
  highlight?: boolean
}) {
  const classnames = gridItemRecipe({ highlight })
  return (
    <Box className={cx(classnames, className)}>
      <StyledHeading>
        <a href={href} target='_blank' className={classes.item_titleLink}>
          {title}
        </a>
      </StyledHeading>
      {description && <p className={classes.item_description}>{description}</p>}
    </Box>
  )
}

export const DocGrid = Object.assign(DocGridRoot, {
  Link: DocLink,
  ExternalLink: ExternalLink,
})

function StyledHeading({ children, ...props }: HeadingProps) {
  return (
    <Heading as='h4' className={classes.item_title} {...props}>
      {children}
    </Heading>
  )
}
